import { mapZeroValuesToNull } from '@/modules/proforma/utils/proforma-utils';
import { apiGet } from '@/store/api';
import { GatResponseResult } from '@/types/GatResponseResult';
import {
  ProformaCurrency,
  ProformaInfo,
  ProformaLine,
  ProformaMultiplierItem,
  ProformaWindow,
} from '@/types/ProformaTypes';

export interface ProformaInfoFromBackend {
  ID: number;
  VESSEL_NAME: string;
  VESSEL_ID: number;
  HARBOUR_ID: number | undefined;
  QUAY_ID: number | undefined;
  CLIENT_ID: number | undefined;
  VESSEL_TYPE: string | undefined;
  ETA: string | undefined;
  ETD: string | undefined;
  PROFORMA_NUMBER: string;
  COMMENTS_NEW: string;
  DESCRIPTION: string;
  CUR_CODE1: string;
  CUR_CODE2: string | undefined;
  CUR_CODE3: string | undefined;
  CUR_CODE4: string | undefined;
  SUBTOTAL_IN: number;
  PORTCALL_ID: number;
  AGENCY_ID: number;
  GT: number | undefined;
  NT: number | undefined;
  LOA: number | undefined;
  BEAM: number | undefined;
  DWT: number | undefined;
  LOADED_Q: number | undefined;
  DISCH_Q: number | undefined;
  SUMMERDRAFT: number | undefined;
  DRAFT_ARRIVAL: number | undefined;
  DRAFT_DEPARTURE: number | undefined;
  CSI: number | undefined;
  ESI: number | undefined;
  PAX_CAP: number | undefined;
  SBT: number | undefined;
  CBM: number | undefined;
  USER_FIELD1: string | undefined;
  LOCAL_CURRENCY: string;
  agency: string | undefined;
  harbour: string | undefined;
  client: string | undefined;
  quay: string | undefined;
  vesselType: string | undefined;
  SEDNA_JOB_ID: string | undefined;
}

export interface ProformaLineFromBackend {
  /** Unique identifier for the item */
  ID: number;
  /** Describes the price item (e.g., "Pilotage in") */
  PRICE_TEXT: string;
  /** Quantity for "in" items */
  QUANTITY_IN: number;
  /** Optional label for the quantity (can be empty) */
  QUANTITY_LABEL: string;
  /** Rate for the item */
  RATE: number;
  /** Currency of the item */
  CURRENCY_CODE: string;
  /** Multiplier for the item */
  MULTIPLE: number;
  /** Rebate percentage (negative value) */
  REBATE_IN: number | undefined;
  /** Calculated rebate amount */
  REBATE_AMOUNT: number | undefined;
  /** VAT percentage */
  VAT_PERCENT: number;
  /** Calculated VAT amount */
  VAT: number;
  /** Optional comments (can be empty) */
  COMMENTS: string;
  /** Description of the tariff (can be empty) */
  TARIFF_DESCRIPTION: string;
  /** Sorting order for the item */
  SORT_ORDER: number;
  /** Total amount after rebate and VAT */
  AMOUNT_IN: number;
  /** Multiplier items as a string or null */
  MULTIPLIER_ITEMS: string | null;
  /** Indicates if the quantity checkbox is used (1 = true, 0 = false) */
  USE_QTY_CHECKBOX: 1 | 0;
}

export interface ProformaWindowFromBackend {
  proformaInfo: ProformaInfoFromBackend;
  currencies: ProformaCurrency[];
  proformaLines: ProformaLineFromBackend[];
}

function mapProformaInfo(proformaInfo: ProformaInfoFromBackend): ProformaInfo {
  const agency = proformaInfo.agency ? (JSON.parse(proformaInfo.agency) as ProformaInfo['agency']) : undefined;
  const harbour = proformaInfo.harbour ? (JSON.parse(proformaInfo.harbour) as ProformaInfo['harbour']) : undefined;
  const client = proformaInfo.client ? (JSON.parse(proformaInfo.client) as ProformaInfo['client']) : undefined;
  const quay = proformaInfo.quay ? (JSON.parse(proformaInfo.quay) as ProformaInfo['quay']) : undefined;
  const vesselType = proformaInfo.vesselType
    ? (JSON.parse(proformaInfo.vesselType) as ProformaInfo['vesselType'])
    : undefined;
  const result: ProformaInfo = {
    ...proformaInfo,
    agency,
    harbour,
    client,
    quay,
    vesselType,
  };
  return result;
}

function mapToProformaLines(profomralines: ProformaLineFromBackend[]): ProformaLine[] {
  const result: ProformaLine[] = profomralines.map((line) => {
    // Parse multiplier items hvis de ikke er null.
    const newLine = line as unknown as any;
    const multiplierItems: ProformaMultiplierItem[] = line.MULTIPLIER_ITEMS ? JSON.parse(line.MULTIPLIER_ITEMS) : [];

    newLine.MULTIPLIER_ITEMS = multiplierItems;
    newLine.USE_QTY_CHECKBOX = line.USE_QTY_CHECKBOX === 1;
    return newLine as ProformaLine;
  });

  return result;
}

export async function getProforma(proformaId: string | number): Promise<ProformaWindow> {
  const response = (await apiGet(`proforma/${proformaId}`)) as GatResponseResult<ProformaWindowFromBackend>;
  if (!response.payload) {
    throw new Error('No payload in response');
  }
  const lines = mapZeroValuesToNull(response.payload.proformaLines);
  return {
    proformaInfo: mapProformaInfo(response.payload.proformaInfo),
    currencies: response.payload.currencies,
    proformaLines: mapToProformaLines(lines),
  };
}
